import { differenceInDays } from 'date-fns';
import { PairLadderWithMovement } from '@/types/pairsLadder';
import { ChallengeSuggestion, ChallengeMatch } from '@/types/challengeMatch';

export interface ComputeChallengeSuggestionsParams {
  ladder: PairLadderWithMovement[];
  userPairIds: string[];
  challengeRange: number;
  existingChallenges: ChallengeMatch[];
  autoSuggestEnabled: boolean;
  limit?: number;
}

/**
 * Pure suggestion rules shared by the group challenge section and the
 * home-screen challenge widget. Suggests challenging pairs ranked ABOVE the
 * user's pair, within the group's configured rank range.
 */
export function computeChallengeSuggestions({
  ladder,
  userPairIds,
  challengeRange,
  existingChallenges,
  autoSuggestEnabled,
  limit = 5,
}: ComputeChallengeSuggestionsParams): ChallengeSuggestion[] {
  if (!autoSuggestEnabled || userPairIds.length === 0) return [];

  const rankedPairs = ladder.filter(p => p.ladder_rank > 0);
  const activeChallengeIds = new Set(
    existingChallenges
      .filter(c => c.status === 'pending' || c.status === 'accepted')
      .flatMap(c => [c.challenger_pair_id, c.challenged_pair_id])
  );

  const suggestions: ChallengeSuggestion[] = [];

  for (const userPairId of userPairIds) {
    const userPair = rankedPairs.find(p => p.id === userPairId);
    if (!userPair) continue;

    for (const target of rankedPairs) {
      if (target.id === userPairId) continue;
      if (activeChallengeIds.has(target.id)) continue;

      // Skip if any player appears in both pairs
      const userPlayerIds = new Set([userPair.player1_id, userPair.player2_id]);
      if (userPlayerIds.has(target.player1_id) || userPlayerIds.has(target.player2_id)) continue;

      // Only suggest challenging pairs ABOVE (lower rank number)
      if (target.ladder_rank >= userPair.ladder_rank) continue;
      const rankGap = userPair.ladder_rank - target.ladder_rank;
      if (rankGap > challengeRange) continue;

      let reason: ChallengeSuggestion['reason'] = 'proximity';
      if (target.last_match_date) {
        const daysSince = differenceInDays(new Date(), new Date(target.last_match_date));
        if (daysSince >= 7) reason = 'inactive';
      }

      suggestions.push({
        challengerPair: {
          id: userPair.id,
          ladder_rank: userPair.ladder_rank,
          ladder_points: userPair.ladder_points,
          matches_played: userPair.matches_played,
          wins: userPair.wins,
          losses: userPair.losses,
          player1_id: userPair.player1_id,
          player2_id: userPair.player2_id,
          player1: userPair.player1,
          player2: userPair.player2,
        },
        challengedPair: {
          id: target.id,
          ladder_rank: target.ladder_rank,
          ladder_points: target.ladder_points,
          matches_played: target.matches_played,
          wins: target.wins,
          losses: target.losses,
          player1_id: target.player1_id,
          player2_id: target.player2_id,
          player1: target.player1,
          player2: target.player2,
        },
        rankGap,
        reason,
      });
    }
  }

  // Sort: inactive targets first, then by rank gap
  suggestions.sort((a, b) => {
    if (a.reason === 'inactive' && b.reason !== 'inactive') return -1;
    if (b.reason === 'inactive' && a.reason !== 'inactive') return 1;
    return a.rankGap - b.rankGap;
  });

  return suggestions.slice(0, limit);
}
